'use client';

import { createContext, type ReactNode, useContext, useMemo, useRef } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { useStore } from 'zustand';
import {
  createLayoutDimensionsStore,
  LayoutDimensionsState,
  LayoutDimensionsStore,
} from '@/lib/store/layout-dimensions-store';
import { Dimensions } from '@/lib/types/dimensions';

type LayoutDimensionsStoreApi = ReturnType<typeof createLayoutDimensionsStore>;

const LayoutDimensionsStoreContext = createContext<LayoutDimensionsStoreApi | undefined>(undefined);

type LayoutDimensionsStoreProviderProps = Partial<LayoutDimensionsState> & {
  children: ReactNode;
};

export const LayoutDimensionsStoreProvider = ({
  children,
  ...storeInit
}: LayoutDimensionsStoreProviderProps) => {
  const storeRef = useRef<LayoutDimensionsStoreApi | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createLayoutDimensionsStore(storeInit);
  }

  return (
    <LayoutDimensionsStoreContext.Provider value={storeRef.current}>
      {children}
    </LayoutDimensionsStoreContext.Provider>
  );
};

export const useLayoutDimensionsStore = (): LayoutDimensionsStore => {
  const layoutDimensionsStoreContext = useContext(LayoutDimensionsStoreContext);

  if (!layoutDimensionsStoreContext) {
    throw new Error(`useLayoutDimensionsStore must be used within LayoutDimensionsStoreProvider`);
  }

  return useStore(layoutDimensionsStoreContext);
};

export const useContentDimensions = (): Dimensions => {
  const { navbarDimensions, contentWidth } = useLayoutDimensionsStore();
  const { height: windowHeight } = useWindowSize();
  const navbarHeight = navbarDimensions?.height ?? 0;

  return useMemo(() => {
    return { width: contentWidth ?? 0, height: windowHeight - navbarHeight };
  }, [windowHeight, navbarHeight, contentWidth]);
};
